import { type ReactNode, useEffect, useRef } from 'react'

import { useLocation } from 'react-router-dom'

import { logger } from './LoggerService'
import { mountConsoleErrorInterceptor } from './interceptors/consoleErrorInterceptor'
import { mountContextBridge } from './interceptors/contextBridge'
import { mountErrorInterceptor } from './interceptors/errorInterceptor'
import {
  getLastNavRecord,
  mountHistoryInterceptor
} from './interceptors/historyInterceptor'
import { mountLifecycleInterceptor } from './interceptors/lifecycleInterceptor'
import { mountLocalStorageInterceptor } from './interceptors/localStorageInterceptor'
import { mountNetworkInterceptor } from './interceptors/networkInterceptor'
import { mountPerformanceInterceptor } from './interceptors/performanceInterceptor'

type Props = {
  children: ReactNode
  userId?: number
  organizationId?: number
}

export function LoggerProvider({ children, userId, organizationId }: Props) {
  useEffect(() => {
    if (userId != null && organizationId != null) {
      logger.setActor(userId, organizationId)
    } else {
      logger.clearActor()
    }
  }, [userId, organizationId])

  useEffect(() => {
    const cleanupBridge = mountContextBridge(logger)
    const cleanupConsoleErrors = mountConsoleErrorInterceptor(logger)
    const cleanupErrors = mountErrorInterceptor(logger)
    const cleanupStorage = mountLocalStorageInterceptor(logger)
    const cleanupHistory = mountHistoryInterceptor()
    const cleanupLifecycle = mountLifecycleInterceptor(logger)
    const cleanupPerformance = mountPerformanceInterceptor(logger)
    const cleanupNetwork = mountNetworkInterceptor(logger, {
      logSuccessfulUrlPrefixes: [process.env.REACT_APP_AUTH_ENDPOINT ?? '']
    })
    logger.track('system.session_start', {
      url: window.location.href.split('?')[0]
    })
    return () => {
      cleanupErrors()
      cleanupConsoleErrors()
      cleanupStorage()
      cleanupHistory()
      cleanupLifecycle()
      cleanupPerformance()
      cleanupNetwork()
      cleanupBridge()
    }
  }, [])

  const location = useLocation()
  const prevLocationRef = useRef({
    pathname: location.pathname,
    search: location.search,
    hash: location.hash
  })
  useEffect(() => {
    const previous = prevLocationRef.current
    const current = {
      pathname: location.pathname,
      search: location.search,
      hash: location.hash
    }
    prevLocationRef.current = current
    if (
      previous.pathname === current.pathname &&
      previous.search === current.search &&
      previous.hash === current.hash
    )
      return
    const nav = getLastNavRecord()
    logger.track('navigation.route_changed', {
      from: previous.pathname,
      to: current.pathname,
      queryChanged: previous.search !== current.search,
      hashChanged: previous.hash !== current.hash,
      action: nav?.action ?? 'pop',
      stack: nav?.stack
    })
  }, [location.pathname, location.search, location.hash])

  return children
}
